/* ==========================================================================
   Session Events
   Task F26: Event system for session-related events like expiry
   ========================================================================== */

/**
 * Custom event name for provider session expiry.
 * Dispatched when a 401 response is received from the provider API.
 */
export const SESSION_EXPIRED_EVENT = 'tjm:session-expired'

/**
 * Custom event name for admin session expiry.
 * Dispatched when a 401 response is received from the admin API.
 */
export const ADMIN_SESSION_EXPIRED_EVENT = 'tjm:admin-session-expired'

/**
 * What the 401 interceptor knows about why the session ended.
 *
 * Carried as structured data rather than a bare string so the modal can branch on
 * intent instead of matching on copy — reworded text must never silently change
 * behaviour.
 */
export interface SessionExpiredDetail {
  /** Overrides the modal's default explanation (e.g. the stale-role 401). */
  message?: string
  /**
   * True when re-authenticating here cannot possibly succeed — the account was
   * disabled, so no password will work until an admin re-enables it. The modal
   * drops its login form and offers only a way out.
   */
  terminal?: boolean
}

/**
 * Helper to create session event utilities.
 */
function createSessionEventHelper(eventName: string) {
  return {
    /**
     * Dispatch a session expired event.
     * Components listening for this event will show the re-login modal.
     */
    dispatch(detail?: SessionExpiredDetail): void {
      window.dispatchEvent(new CustomEvent(eventName, { detail }))
    },
  }
}

/**
 * Provider session expired event helper.
 */
export const sessionExpiredEvent = createSessionEventHelper(SESSION_EXPIRED_EVENT)

/**
 * Admin session expired event helper.
 */
export const adminSessionExpiredEvent = createSessionEventHelper(ADMIN_SESSION_EXPIRED_EVENT)
